import React, { useContext, useEffect, useState, useCallback } from 'react';
import { 
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Image,
  Link,
  Container,
  VStack,
  HStack,
  Stack,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  Show,
  Hide,
  Flex,
  StackDivider,
  Skeleton,
  SkeletonText,
  useDisclosure
} from '@chakra-ui/react';
import { ConfigContext } from './utils/Config';
import { useTranslation } from 'react-i18next';
import { Search, RefreshCw, LayoutList, List, Scale } from 'lucide-react';
import fetchData from "./utils/Util";
import FavoriteButton from './utils/FavoriteButton';
import ImageGallery from './utils/ImageGallery';
import { ItemLinkChakra } from './utils/ItemLink';


const CompareWidget = ({ selectedItems, onCompare, t }) => {
  if (selectedItems.length === 0) return null;

  return (
    <Box
      position="fixed"
      left="0"
      top="50%"
      transform="translateY(-50%)"
      bg="white"                    
      color="gray.700"          
      p={4}
      borderWidth="1px"            
      borderLeft="0"               
      borderColor="gray.200"       
      borderRadius="0 lg lg 0"
      shadow="md"                  
      zIndex={1000}
    >
      <VStack spacing={2}>
        <Text fontSize="sm">
          {selectedItems.length === 1 
            ? t('myFavorites.selectMoreItems')
            : t('myFavorites.selectedItems', { count: selectedItems.length })}
        </Text>
        <Button
          onClick={onCompare}
          isDisabled={selectedItems.length < 2}
          colorScheme="blue"        
          variant="solid"           
          size="sm"
          leftIcon={<Scale size={16} />}
        >
          {t('myFavorites.compareItems')}
        </Button>
        {selectedItems.length >= 4 && (
          <Text fontSize="xs" color="gray.500"> 
            {t('myFavorites.maxItemsReached')}
          </Text>
        )}
      </VStack>
    </Box>
  );
};

const CompareModal = ({ isOpen, onClose, items, t }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader>{t('myFavorites.CompareItems')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table>
            <Tbody>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.image')}</Td>
                {items.map(item => (
                  <Td key={item.id}>
                    {item.image_gallery && item.image_gallery.images_path && item.image_gallery.images_path.length > 0 && (
                      <Image
                        src={item.image_gallery.images_path[0].big}
                        alt={`Lot ${item.lot_number}`}
                        objectFit="contain"
                        h="200px"
                        w="100%"
                      />
                    )}                    
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.lot')}</Td>
                {items.map(item => (
                  <Td key={item.id}>{item.lot_number}</Td>
                ))}
              </Tr>
              {items.some(item => item.show_title) && (
                <Tr>
                  <Td fontWeight="bold">{t('myFavorites.title')}</Td>
                  {items.map(item => (
                    <Td key={item.id}>{item.title}</Td>
                  ))}
                </Tr>
              )}
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.startPrice')}</Td>
                {items.map(item => (
                  <Td key={item.id}>{item.opening_price}</Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.estimate')}</Td>
                {items.map(item => (
                  <Td key={item.id}>
                    {item.estimation || '-'}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.soldFor')}</Td>
                {items.map(item => (
                  <Td key={item.id}>
                    {item.soldfor}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.category')}</Td>
                {items.map(item => (
                  <Td key={item.id}>{item.categories || '-'}</Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.catalogInfo')}</Td>
                {items.map(item => (
                  <Td key={item.id}>
                    {item.item_taxonomy?.catalogue_name_and_number?.rendered ? (
                      <Box dangerouslySetInnerHTML={{ __html: item.item_taxonomy.catalogue_name_and_number.rendered }} />
                    ) : '-'}
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.symbols')}</Td>
                {items.map(item => (
                  <Td key={item.id} className="catalog-symbols aukphila">{item.item_taxonomy?.symbols?.rendered || '-'}</Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold" verticalAlign="top">{t('myFavorites.description')}</Td>
                {items.map(item => (
                  <Td key={item.id} verticalAlign="top">
                    <Box dangerouslySetInnerHTML={{ __html: item.body || '-' }} />
                  </Td>
                ))}
              </Tr>
              <Tr>
                <Td fontWeight="bold">{t('myFavorites.saleTitle')}</Td>
                {items.map(item => (
                  <Td key={item.id}>{item.sale_title}</Td>
                ))}
              </Tr>
            </Tbody>
          </Table>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const MyFavorites = ({ language }) => {
  const {t, i18n} = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lot-asc');
  const [selectedSale, setSelectedSale] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewType, setViewType] = useState('full');
  const config = useContext(ConfigContext);
  const toast = useToast();
  const [selectedToCompare, setSelectedToCompare] = useState([]);
  const {isOpen: isCompareOpen, onOpen: onCompareOpen, onClose: onCompareClose} = useDisclosure();
  const [sales, setSales] = useState([{id: 0, uuid: 'all', title: t('myFavorites.allSales')}]);
  const [unfollowedIds, setUnfollowedIds] = useState(new Set());


  // Fetch sales list.
  useEffect(() => {
    if (!config.accessToken) return;
    if (!config.userData) return;

    const fetchSales = async () => {
      const data = config.userData;
      if (data.website_bidder_info && Array.isArray(data.website_bidder_info)) {
        const fetchedSales = data.website_bidder_info.map(info => ({
          id: info.sale,
          uuid: info.sale_uuid,
          title: info.sale_title || `Sale ${info.id}`,
          status: info.sale_status
        }));

        setSales([
          {id: 0, uuid: 'all', title: t('myFavorites.allSales')},
          ...fetchedSales
        ]);
      }
    };

    fetchSales();
  }, [config.accessToken, config.siteData, config.siteData.backoffice_url, config.userData, toast, t]);

  // Fetch favorites items.
  const fetchFavorites = useCallback(async (saleUuid = 'all') => {
    setIsLoading(true);
    try {
      // First fetch favorite items
      let url = `${config.backendUrl}/api/item_flaggings?access_token=${config.accessToken}`;
      if (saleUuid !== 'all') {
        url += `&sale_uuid=${saleUuid}`;
      }

      const response = await fetchData(url, {
        mockData: 'api-items-favorites',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }

      const flaggingsResult = await response.json();
      const favoriteItems = flaggingsResult.data || [];

      // If we have favorite items, fetch their details
      if (favoriteItems.length > 0) {
        // Get the uuids of all favorite items
        const itemUuids = favoriteItems.map(item => item.item_uuid);

        // Fetch item details using items-json endpoint
        const itemsResponse = await fetchData(
            `${config.siteData.backoffice_url}/api/items-json`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                json_type: 'web',
                uuids: itemUuids,
                language: i18n.language === 'zh' ? 'zh-hans' : i18n.language
              })
            }
        );

        if (!itemsResponse.ok) {
          throw new Error('Failed to fetch item details');
        }

        const itemsResult = await itemsResponse.json();

        // Merge flagging data with item details
        const mergedItems = favoriteItems.map(favorite => {
          const itemDetails = itemsResult.data.find(item => item.uuid === favorite.item_uuid);
          return {
            ...favorite,
            ...itemDetails
          };
        });

        setFavorites(mergedItems);
      } else {
        setFavorites([]);
      }
      setIsLoading(false);
    } catch (error) {
      toast({
        title: t('myFavorites.errorFetching'),
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  },
      [config.backendUrl, config.siteData.backoffice_url, config.accessToken, i18n.language, toast, t]);

  // Fetch favorites items.
  useEffect(() => {
    if (!config.accessToken) return;
    if (!config.userData) return;

    fetchFavorites(selectedSale);
  }, [selectedSale, fetchFavorites, config.accessToken, config.userData]);

  const handleCompareToggle = (item) => {
    setSelectedToCompare(prev => {
      if (prev.find(i => i.id === item.id)) {
        return prev.filter(i => i.id !== item.id);
      }
      if (prev.length >= 4) {
        toast({
          title: t('myFavorites.maxItemsReached'),
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return prev;
      }
      return [...prev, item];
    });
  };

  const Pagination = ({currentPage, totalPages, onPageChange, totalItems, itemsPerPage}) => {
    const startItem = ((currentPage - 1) * itemsPerPage) + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <HStack w="full" justify="space-between" mt={4} mb={4}>
          <Text fontSize="sm" color="gray.600" flex="1">
            {t('pagination.showing', {
              start: startItem,
              end: endItem,
              total: totalItems
            })}
          </Text>

          <HStack spacing={2} flex="2" justify="center">
            <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
            >
              {t('pagination.previous')}
            </Button>

            {Array.from({length: totalPages}, (_, i) => i + 1).map((page) => (
                <Button
                    key={page}
                    size="sm"
                    variant={currentPage === page ? "solid" : "outline"}
                    colorScheme="blue"
                    onClick={() => onPageChange(page)}
                >
                  {page}
                </Button>
            ))}

            <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
            >
              {t('pagination.next')}
            </Button>
          </HStack>

          <Box flex="1"/>
        </HStack>
    );
  };

  const filteredItems = favorites
    .filter(item => {
      const matchesSearch = 
          item.body?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.lot_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSale = selectedSale === 'all' || item.sale_uuid === selectedSale;
      return matchesSearch && matchesSale;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'lot-asc':
          return parseInt(a.lot_number) - parseInt(b.lot_number);
        case 'lot-desc':
          return parseInt(b.lot_number) - parseInt(a.lot_number);
        case 'price-asc':
          // Replace thousands separator and remove non-numeric characters
          const priceA = parseFloat(a.opening_price.replace(/\./g, '').replace(/[^\d]/g, ''));
          const priceB = parseFloat(b.opening_price.replace(/\./g, '').replace(/[^\d]/g, ''));
          return priceA - priceB;
        case 'price-desc':
          // Replace thousands separator and remove non-numeric characters
          const descPriceA = parseFloat(a.opening_price.replace(/\./g, '').replace(/[^\d]/g, ''));
          const descPriceB = parseFloat(b.opening_price.replace(/\./g, '').replace(/[^\d]/g, ''));
          return descPriceB - descPriceA;
        default:
          return 0;
      }
    })

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TableSkeleton = () => (
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th width="50px"><Skeleton height="20px" width="20px"/></Th>
              <Th><Skeleton height="20px" width="100px"/></Th>
              <Th><Skeleton height="20px" width="200px"/></Th>
              <Th><Skeleton height="20px" width="100px"/></Th>
              <Th><Skeleton height="20px" width="150px"/></Th>
              <Th><Skeleton height="20px" width="100px"/></Th>
              <Th><Skeleton height="20px" width="150px"/></Th>
              <Th><Skeleton height="20px" width="100px"/></Th>
            </Tr>
          </Thead>
          <Tbody>
            {Array.from({length: 5}).map((_, index) => (
                <Tr key={index}>
                  <Td><Skeleton height="20px" width="20px"/></Td>
                  <Td>
                    <Flex align="center" gap={4}>
                      <Skeleton height="50px" width="50px"/>
                      <Skeleton height="20px" width="60px"/>
                    </Flex>
                  </Td>
                  <Td><Skeleton height="20px" width="200px"/></Td>
                  <Td><Skeleton height="20px" width="80px"/></Td>
                  <Td><Skeleton height="20px" width="120px"/></Td>
                  <Td><Skeleton height="20px" width="80px"/></Td>
                  <Td><Skeleton height="20px" width="120px"/></Td>
                  <Td>
                    <HStack spacing={2}>
                      <Skeleton height="30px" width="80px"/>
                      <Skeleton height="30px" width="80px"/>
                    </HStack>
                  </Td>
                </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
  );

  if (isLoading) {
    return (
      <Container maxW="7xl" py={6}>
        {/* Form Controls Skeletons */}
        <VStack spacing={4} mb={6} align="stretch">
          {/* Sale Select Skeleton */}
          <Box>
            <Skeleton height="20px" width="100px" mb={2}/>
            <Skeleton height="40px" width="300px"/>
          </Box>

          {/* Filters Row Skeleton */}
          <HStack w="full" spacing={6} align="flex-end">
            <Skeleton height="40px" width="250px"/>
            <Skeleton height="40px" width="200px"/>
            <Skeleton height="40px" width="150px"/>
            <Skeleton height="20px" width="150px"/>
          </HStack>
        </VStack>

        {/* Table or List Skeleton based on view type */}
        {viewType === 'table' ? (
            <TableSkeleton/>
        ) : (
            Array.from({length: 3}).map((_, index) => (
                <Box
                    key={index}
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    mb={4}
                >
                  <Flex gap={4}>
                    {/* Image Skeleton */}
                    <Skeleton height="200px" width="200px"/>

                    {/* Content Skeleton */}
                    <Box flex="1">
                      <SkeletonText mt='4' noOfLines={1} spacing='4' width="200px"/>
                      <SkeletonText mt='4' noOfLines={3} spacing='4'/>
                      <Flex gap={6} mt={4}>
                        <Skeleton height="20px" width="120px"/>
                        <Skeleton height="20px" width="120px"/>
                        <Skeleton height="20px" width="120px"/>
                      </Flex>
                      <SkeletonText mt='4' noOfLines={4} spacing='4'/>
                    </Box>

                    {/* Actions Skeleton */}
                    <VStack spacing={2} ml={4} width="120px">
                      <Skeleton height="30px" width="full"/>
                      <Skeleton height="30px" width="full"/>
                      <Skeleton height="30px" width="full"/>
                    </VStack>
                  </Flex>
                </Box>
            ))
        )}
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={6}>
      <CompareWidget
        selectedItems={selectedToCompare}
        onCompare={onCompareOpen}
        t={t}
      />
      <VStack spacing={6} align="stretch">
        {/* Filters Section */}
        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
          {/* Sale Select */}
          <FormControl maxW={{ base: "full", md: "300px" }}>
            <FormLabel>{t('filters.saleLabel')}</FormLabel>
            <Select
              value={selectedSale}
              onChange={(e) => setSelectedSale(e.target.value)}
            >
              {sales.map(sale => (
                <option key={sale.id} value={sale.uuid}>
                  {sale.title}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Search Box */}
          <FormControl maxW={{ base: "full", md: "250px" }}>
            <FormLabel>{t('filters.searchLabel')}</FormLabel>
            <Box position="relative">
              <Input
                placeholder={t('search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                pl={10}
                width="auto"
              />
              <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                <Search size={20}/>
              </Box>
            </Box>
          </FormControl>

          {/* Sort Select */}
          <FormControl maxW={{ base: "full", md: "200px" }}>
            <FormLabel>{t('filters.sortLabel')}</FormLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {[
                {value: 'lot-asc', label: t('sort.lotAsc')},
                {value: 'lot-desc', label: t('sort.lotDesc')},
                {value: 'price-asc', label: t('sort.priceAsc')},
                {value: 'price-desc', label: t('sort.priceDesc')}
              ].map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Items per page */}
          <FormControl maxW={{ base: "full", md: "150px" }}>
            <FormLabel>{t('filters.itemsPerPage')}</FormLabel>
            <Select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              {[12, 24, 36, 48].map(num => (
                <option key={num} value={num}>
                  {num} per page
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Reset Button */}
          <Button
            variant="outline"
            onClick={() => {
              setSearchTerm('');
              setSortBy('lot-asc');
              setItemsPerPage(12);
              setSelectedToCompare([]);
            }}
            leftIcon={<RefreshCw size={16}/>}
            alignSelf={{ base: "flex-start", md: "flex-end" }}
            mt={{ base: 2, md: 8 }}
          >
            {t('filters.reset')}
          </Button>
        </Stack>

        {/* View Type Toggles */}
        <HStack spacing={4} ml={4}>
          <Link
            onClick={() => setViewType('full')}
            color={viewType === 'full' ? 'blue.500' : 'gray.500'}
            textDecoration={viewType === 'full' ? 'underline' : 'none'}
            cursor="pointer"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <LayoutList size={16}/> {t('myBids.detailedView')}
          </Link>
          <Link
            onClick={() => setViewType('table')}
            color={viewType === 'table' ? 'blue.500' : 'gray.500'}
            textDecoration={viewType === 'table' ? 'underline' : 'none'}
            cursor="pointer"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <List size={16}/> {t('myBids.summaryView')}
          </Link>
        </HStack>

        {/* Content Area */}
        {paginatedItems.length === 0 ? (
          <Box textAlign="center" py={10}>
            {favorites.length === 0 ? (
              <VStack spacing={2}>
                <Text fontSize="xl" color="gray.600">
                  {t('myFavorites.noFavorites')}
                </Text>
                <Text fontSize="md" color="gray.500">
                  {t('myFavorites.addFavoritesHint')}
                </Text>
              </VStack>
            ) : (
              <Text fontSize="xl" color="gray.600">
                {t('myFavorites.noMatchingFavorites')}
              </Text>
            )}
          </Box>
        ) : viewType === 'table' ? (
          <>
            {/* Desktop Table View */}
            <Hide below="md">
              <Box borderWidth="1px" borderRadius="lg" overflow="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th width="50px"></Th>
                      <Th>{t('myFavorites.lot')}</Th>
                      {paginatedItems.some(item => item.show_title) && (
                        <Th>{t('myFavorites.title')}</Th>
                      )}
                      <Th>{t('myFavorites.startPrice')}</Th>
                      <Th>{t('myFavorites.estimate')}</Th>
                      <Th>{t('myFavorites.soldFor')}</Th>
                      <Th>{t('myFavorites.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedItems.map((item) => (
                      <Tr key={item.id} opacity={unfollowedIds.has(item.item_id) ? 0.5 : 1}>
                        <Td>
                          <Hide below="md">
                            <Checkbox
                              isChecked={selectedToCompare.some(i => i.id === item.id)}
                              onChange={() => handleCompareToggle(item)}
                              isDisabled={selectedToCompare.length >= 4 && !selectedToCompare.some(i => i.id === item.id)}
                            />
                          </Hide>                          
                        </Td>
                        <Td>
                          <ItemLinkChakra itemUuid={item.uuid} color="blue.500">
                            {item.lot_number}
                          </ItemLinkChakra>
                        </Td>
                        {paginatedItems.some(item => item.show_title) && (
                          <Td maxW="300px" isTruncated>{item.title}</Td>
                        )}
                        <Td>{item.opening_price}</Td>
                        <Td>{item.estimation || '-'}</Td>
                        <Td>{item.soldfor || '-'}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <FavoriteButton
                              itemId={item.item_id}
                              initialIsFollowing={!unfollowedIds.has(item.item_id)}
                              onFavoriteChange={(isFollowing) => {
                                if (isFollowing) {
                                  setUnfollowedIds(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(item.item_id);
                                    return newSet;
                                  });
                                } else {
                                  setUnfollowedIds(prev => {
                                    const newSet = new Set(prev);
                                    newSet.add(item.item_id);
                                    return newSet;
                                  });
                                }
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Hide>

            {/* Mobile Card View */}
            <Show below="md">
              <Stack spacing={4}>
                {paginatedItems.map((item) => (
                  <Card key={item.id}>
                    <CardBody>
                      <Stack divider={<StackDivider />} spacing={3}>
                        <Flex justify="space-between" align="center">
                          <ItemLinkChakra itemUuid={item.uuid} color="blue.500">
                            {t('myFavorites.lot')} {item.lot_number}
                          </ItemLinkChakra>
                          <Hide below="md">
                            <Checkbox
                              isChecked={selectedToCompare.some(i => i.id === item.id)}
                              onChange={() => handleCompareToggle(item)}
                              isDisabled={selectedToCompare.length >= 4 && !selectedToCompare.some(i => i.id === item.id)}
                            />
                          </Hide>                          
                        </Flex>

                        {item.show_title && (
                          <Text fontWeight="medium">{item.title}</Text>
                        )}

                        <Stack spacing={2}>
                          <Flex justify="space-between">
                            <Text fontWeight="medium">{t('myFavorites.startPrice')}:</Text>
                            <Text>{item.opening_price}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontWeight="medium">{t('myFavorites.estimate')}:</Text>
                            <Text>{item.estimation || '-'}</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontWeight="medium">{t('myFavorites.soldFor')}:</Text>
                            <Text>{item.soldfor || '-'}</Text>
                          </Flex>
                        </Stack>

                        <Box>
                          <FavoriteButton
                            itemId={item.item_id}
                            initialIsFollowing={!unfollowedIds.has(item.item_id)}
                            onFavoriteChange={(isFollowing) => {
                              if (isFollowing) {
                                setUnfollowedIds(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(item.item_id);
                                  return newSet;
                                });
                              } else {
                                setUnfollowedIds(prev => {
                                  const newSet = new Set(prev);
                                  newSet.add(item.item_id);
                                  return newSet;
                                });
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                    </CardBody>
                  </Card>
                ))}
              </Stack>
            </Show>
          </>
        ) : (
          <Box p={4} borderWidth="1px" borderRadius="lg">
            {paginatedItems.map((item) => (
                <Box key={item.id} className="lot-info" mb={4} opacity={unfollowedIds.has(item.item_id) ? 0.5 : 1}>
                  <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                    <Box className="lot-image" flex={{ base: '1', md: '0 0 200px' }}>
                      <ImageGallery imageGallery={item.image_gallery} />
                    </Box>

                    <Flex flex="1" direction="column">
                      <Flex justify="space-between">
                        <Box flex="1">
                          {/* First row: Lot number and title */}
                          <Flex align="stretch" gap={4} mb={4}>
                            <Flex align="center">
                              <ItemLinkChakra itemUuid={item.uuid} color="blue.500" fontSize="lg" fontWeight="bold">
                                {t('myBids.lot')} {item.lot_number}
                              </ItemLinkChakra>
                            </Flex>

                            {item.show_title ? (
                              <Flex align="center">
                                <Text fontSize="lg" fontWeight="bold">
                                  {item.title}
                                </Text>
                              </Flex>
                            ) : null}
                          </Flex>

                          {/* Second row: All prices */}
                          <Flex 
                            gap={6} 
                            mb={4} 
                            direction={{ base: 'column', md: 'row' }}
                            justifyContent={{ base: 'flex-start', md: 'space-between' }}
                          >
                            <Flex gap={2} align="baseline">
                              <Box color="gray.600" fontSize="lg">{t('myFavorites.startPrice')}:</Box>
                              <Box fontSize="lg">{item.opening_price}</Box>
                            </Flex>

                            {(item.estimation) && (
                              <Flex gap={2} align="baseline">
                                <Box color="gray.600" fontSize="lg">{t('myFavorites.estimate')}:</Box>
                                <Box fontSize="lg">
                                  {item.estimation || '-'}
                                </Box>
                              </Flex>
                            )}

                            {item.soldfor !== undefined && item.soldfor !== null && (
                              <Flex gap={2} align="baseline">
                                <Box color="gray.600" fontSize="lg">{t('myFavorites.soldFor')}:</Box>
                                <Box fontSize="lg">{item.soldfor}</Box>
                              </Flex>
                            )}
                          </Flex>

                          {/* Third row: Catalogs and symbols */}
                          <Flex 
                            gap={4} 
                            mb={4} 
                            direction={{ base: 'column', md: 'row' }}
                            justifyContent={{ base: 'flex-start', md: 'space-between' }}
                          >
                            {item.categories && (
                                <Box>
                                  <Box fontSize="md">{item.categories}</Box>
                                </Box>
                            )}
                            {item.item_taxonomy?.catalogue_name_and_number?.rendered && (
                              <Box fontSize="md" dangerouslySetInnerHTML={{ __html: item.item_taxonomy.catalogue_name_and_number.rendered }} />
                            )}
                            {item.item_taxonomy?.symbols?.rendered && (
                              <Box>
                                <Box fontSize="md" className="catalog-symbols aukphila">{item.item_taxonomy.symbols.rendered}</Box>
                              </Box>
                            )}
                          </Flex>


                          {/* Description */}
                          <Box mb={4} className="lot-details">
                            <Box dangerouslySetInnerHTML={{__html: item.body}}/>
                          </Box>

                          {/* Sale information */}
                          <Flex gap={4} color="gray.600" fontSize="sm">
                            <Text>{item.sale_title}</Text>
                            <Text>{item.live_sale_date}</Text>
                          </Flex>
                        

                          <Show below="md">
                            <VStack spacing={2} mt={4} align="stretch">
                              <FavoriteButton
                                itemId={item.item_id}
                                initialIsFollowing={!unfollowedIds.has(item.item_id)}
                                onFavoriteChange={(isFollowing) => {
                                  if (isFollowing) {
                                    setUnfollowedIds(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(item.item_id);
                                      return newSet;
                                    });
                                  } else {
                                    setUnfollowedIds(prev => {
                                      const newSet = new Set(prev);
                                      newSet.add(item.item_id);
                                      return newSet;
                                    });
                                  }
                                }}
                              />
                              <ItemLinkChakra 
                                itemUuid={item.uuid}
                                width="full"
                              >
                                <Button
                                  width="full"
                                  colorScheme="blue"
                                >
                                  {t('myFavorites.viewItem')}
                                </Button>
                              </ItemLinkChakra>
                            </VStack>
                          </Show>
                        </Box>

                        {/* Desktop Buttons */}
                        <Hide below="md">
                          <VStack 
                            spacing={2} 
                            ml={4}
                            justify="center"
                            align="flex-end"
                          >
                            <Checkbox
                              isChecked={selectedToCompare.some(i => i.id === item.id)}
                              onChange={() => handleCompareToggle(item)}
                              isDisabled={selectedToCompare.length >= 4 && !selectedToCompare.some(i => i.id === item.id)}
                            >
                              {t('myFavorites.addToCompare')}
                            </Checkbox>
                            <FavoriteButton
                                itemId={item.item_id}                                
                                onFavoriteChange={(isFollowing) => {
                                  if (isFollowing) {
                                    setUnfollowedIds(prev => {
                                      const newSet = new Set(prev);
                                      newSet.delete(item.item_id);
                                      return newSet;
                                    });
                                  } else {
                                    setUnfollowedIds(prev => {
                                      const newSet = new Set(prev);
                                      newSet.add(item.item_id);
                                      return newSet;
                                    });
                                  }
                                }}
                                initialIsFollowing = {!unfollowedIds.has(item.item_id)}
                            />                            
                            <ItemLinkChakra 
                              itemUuid={item.uuid}
                              width="full"
                            >
                              <Button
                                width="full"
                                colorScheme="blue"
                              >
                                {t('myFavorites.viewItem')}
                              </Button>
                            </ItemLinkChakra>
                          </VStack>
                        </Hide>
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
            ))}
          </Box>
        )}
        

        {/* Compare Modal */}
        <CompareModal
          isOpen={isCompareOpen}
          onClose={onCompareClose}
          items={selectedToCompare}
          t={t}
        />

        {/* Pagination */}
        {paginatedItems.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredItems.length}
            itemsPerPage={itemsPerPage}
          />
        )}
      </VStack>
    </Container>
  );
};

export default MyFavorites;